import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../store';
import { tutorial } from '../tutorial';
import { fmtClock } from '../bridge';
import type { SlotInfo } from '../../save/save-manager';
import { PAUSE_EVENTS } from '../../sim/systems/alertPolicy';
import { IconClose } from './icons';

function Toggle({ checked, onChange, label }: { checked: boolean; onChange(b: boolean): void; label: string }) {
  return (
    <button
      class="toggle" role="switch" aria-checked={checked} aria-label={label}
      onClick={() => { onChange(!checked); }}
    />
  );
}

const ago = (ms: number): string => {
  const m = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${String(m)} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${String(h)} h ago`;
  return `${String(Math.round(h / 24))} d ago`;
};

const slotLine = (info: SlotInfo): string => {
  if (info === null) return 'Empty';
  if (info.corrupt === true) return info.backup ? 'Damaged save - a backup is available' : 'Damaged save';
  return fmtClock(info.tick) + (info.savedAtMs !== undefined ? ` - saved ${ago(info.savedAtMs)}` : '');
};

export function SettingsDrawer() {
  const { settingsOpen, setSettingsOpen, sim, bridge } = useStore();
  const [exportStatus,setExportStatus]=useState('');
  const [exportBusy,setExportBusy]=useState(false);
  const [slots, setSlots] = useState<SlotInfo[] | null>(null);
  const [slotStatus, setSlotStatus] = useState('');
  const [slotBusy, setSlotBusy] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const refreshSlots = () => { setSlots(bridge.listSaveSlots ? bridge.listSaveSlots() : null); };
  useEffect(() => { if (settingsOpen) refreshSlots(); }, [settingsOpen]);
  const [s, setS] = useState({
    audio: true, music: true, metric: true,
    reduceMotion: false, reduceShake: false, noFlash: false,
    colorblind: false, largeText: false,
  });
  if (!settingsOpen) return null;
  const set = (k: keyof typeof s) => (b: boolean) => { setS({ ...s, [k]: b }); };
  const row = (label: string, key: keyof typeof s, hint?: string) => (
    <div class="row" style="padding-left:0;padding-right:0">
      <div class="row-main">
        <div style="font-weight:600;font-size:14.5px">{label}</div>
        {hint && <div class="row-label" style="text-transform:none;letter-spacing:0.01em">{hint}</div>}
      </div>
      <Toggle checked={s[key]} onChange={set(key)} label={label} />
    </div>
  );
  const runSlot = (fn: () => Promise<void>, ok: string) => {
    setSlotBusy(true);
    void fn().then(() => { setSlotStatus(ok); refreshSlots(); }).catch((err: unknown) => {
      setSlotStatus(err instanceof Error && err.message !== '' ? err.message : 'That did not work. Your current game was not changed.');
    }).finally(() => { setSlotBusy(false); setConfirmOverwrite(null); });
  };
  return (
    <>
      <button type="button" class="panel-backdrop" aria-label="Close settings" onClick={() => { setSettingsOpen(false); }} />
      <aside class="drawer" aria-label="Settings">
        <div class="drawer-head">
          <span class="display" style="font-size:19px">Settings</span>
          <button class="icon-btn" aria-label="Close settings" onClick={() => { setSettingsOpen(false); }}><IconClose /></button>
        </div>
        <div class="drawer-body">
          <div class="eyebrow" style="margin-top:6px">Save slots</div>
          <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px">Slot 1 is the autosave: your current game keeps saving there. Before loading or importing, the current game is kept in a separate recovery archive on this device. Normal autosaves do not replace that archive.</div>
          {slots !== null && slots.length === 0 && <div class="row-label" style="margin-top:6px">Slots are not available yet.</div>}
          {(slots ?? []).map((info, i) => (
            <div key={i} class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:6px;padding:8px 12px">
              <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
                <div class="row-value" style="font-size:14px;flex:none;white-space:nowrap">Slot {i + 1}{i === 0 ? ' - Autosave' : ''}</div>
                <div class="row-label">{slotLine(info)}</div>
              </div>
              <div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap">
                <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" disabled={slotBusy || sim.savingDisabled === true || !bridge.saveToSlot} onClick={() => {
                  const fn = bridge.saveToSlot;
                  if (!fn) return;
                  if (info !== null && confirmOverwrite !== i) { setConfirmOverwrite(i); return; }
                  runSlot(() => fn(i), `Saved to slot ${String(i + 1)}.`);
                }}>{info !== null && confirmOverwrite === i ? 'Overwrite?' : 'Save here'}</button>
                <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" disabled={slotBusy || info === null || info.corrupt === true || !bridge.loadFromSlot} onClick={() => {
                  const fn = bridge.loadFromSlot;
                  if (!fn) return;
                  runSlot(() => fn(i), `Loaded slot ${String(i + 1)}${i === 0 ? '' : ' - the previous game is in the device recovery archive'}.`);
                }}>Load</button>
                {info !== null && info.corrupt === true && info.backup && (
                  <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" disabled={slotBusy || !bridge.restoreSlotBackup} onClick={() => {
                    const fn = bridge.restoreSlotBackup;
                    if (!fn) return;
                    runSlot(() => fn(i), `Backup restored for slot ${String(i + 1)}.`);
                  }}>Restore backup</button>
                )}
              </div>
            </div>
          ))}
          {bridge.exportPreviousGame?.() && <button class="btn btn-quiet" style="width:100%;margin-top:8px" onClick={() => {
            const text=bridge.exportPreviousGame?.();if(!text)return;
            const url=URL.createObjectURL(new Blob([text],{type:'application/json'}));const a=document.createElement('a');a.href=url;a.download='exotics-adventure-previous-game.json';a.click();window.setTimeout(()=>{URL.revokeObjectURL(url);},60000);setSlotStatus('Previous game download requested. Import this file to return to it.');
          }}>Export previous game</button>}
          <div class="eyebrow" style="margin-top:12px">Export and import</div>
          <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px">Download the current game as a JSON file, or import one back. A file that is not a valid save is rejected and your current game is not changed.</div>
          <button class="btn btn-primary" style="width:100%;margin-top:8px" disabled={exportBusy||!bridge.exportCurrentSave} onClick={()=>{
            setExportBusy(true);setExportStatus('Preparing current game...');
            const exportSave=bridge.exportCurrentSave;
            if(!exportSave){setExportBusy(false);setExportStatus('Export is not available in this preview.');return;}
            void exportSave().then(text=>{
              const url=URL.createObjectURL(new Blob([text],{type:'application/json'}));
              const a=document.createElement('a');a.href=url;a.download='exotics-adventure-save.json';document.body.append(a);a.click();a.remove();window.setTimeout(()=>{URL.revokeObjectURL(url);},60000);
              setExportStatus('Download requested: exotics-adventure-save.json. Check your browser downloads.');
            }).catch(()=>{setExportStatus('Could not export this game. Your autosave was not changed. Try again when the game is ready.');}).finally(()=>{setExportBusy(false);});
          }}>{exportBusy?'Preparing...':'Export current game'}</button>
          <button class="btn btn-quiet" style="width:100%;margin-top:6px" disabled={slotBusy || sim.savingDisabled === true || !bridge.importSaveFile} onClick={() => { fileRef.current?.click(); }}>Import save file</button>
          <input ref={fileRef} type="file" accept=".json,application/json" style="display:none" onChange={(e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            input.value = '';
            const importFile = bridge.importSaveFile;
            if (!file || !importFile) return;
            setSlotBusy(true);
            void file.text().then((text) => importFile(text)).then(({ tick }) => {
              setSlotStatus(`Imported save loaded (${fmtClock(tick)}). Your previous game is in the device recovery archive.`);
              refreshSlots();
            }).catch(() => {
              setSlotStatus('That file is not a valid save. Your current game was not changed.');
            }).finally(() => { setSlotBusy(false); });
          }} />
          {(exportStatus || slotStatus) && <div role="status" class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px">{[exportStatus, slotStatus].filter(Boolean).join(' ')}</div>}
          <div class="eyebrow" style="margin-top:12px">Critical-event pauses</div>
          <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em">Choose which new events pause time. Off by default until you choose. Turning a pause off never hides the alert. These choices travel with exported game saves.</div>
          {PAUSE_EVENTS.map(event => <div class="row" style="padding-left:0;padding-right:0" key={event}><div class="row-main"><div style="font-weight:600;font-size:14px">{({escape:'Animal escapes',outbreak:'Illness or outbreak',rare_death:'Rare-species deaths',negative_cash:'Cash drops below zero'})[event]}</div></div><Toggle label={`Pause for ${event.replace(/_/g,' ')}`} checked={sim.alertPolicy?.enabled[event] ?? false} onChange={enabled=>{bridge.send('alerts.pause.set',{event,enabled});}} /></div>)}
          {(sim.alertPolicy?.mutedTypes?.length ?? 0)>0 && <div class="row-label" style="white-space:normal;text-transform:none">Muted event types never pause, even when their pause switch is on. Manage mutes in the alerts inbox.</div>}
          {sim.alertPolicy?.lastPause && <div class="row-label" style="white-space:normal;text-transform:none">Last automatic pause: {sim.alertPolicy.lastPause.event.replace(/_/g,' ')} at {fmtClock(sim.alertPolicy.lastPause.tick)}. Use the speed button to resume.</div>}
          <div class="eyebrow" style="margin-top:12px">Audio</div>
          {row('Sound effects', 'audio')}
          {row('Music', 'music')}
          <div class="eyebrow" style="margin-top:10px">Realism</div>
          <div style="display:flex;gap:8px;margin-top:6px">
            {[0, 1, 2].map((lv) => (
              <button key={lv} class={sim.realism === lv ? 'btn btn-primary' : 'btn btn-quiet'} style="flex:1" onClick={() => { bridge.send('realism.set', { level: lv }); }}>Level {lv}</button>
            ))}
          </div>
          <div class="row-label" style="text-transform:none;letter-spacing:0.01em;white-space:normal;margin-top:4px">Higher levels switch on deeper simulation, such as staff depth. Proposed wording.</div>
          <div class="eyebrow" style="margin-top:10px">Units</div>
          {row('Metric units', 'metric', 'Hectares, kilometers, kilograms')}
          <div class="eyebrow" style="margin-top:10px">Accessibility</div>
          {row('Reduce motion', 'reduceMotion')}
          {row('Reduce screen shake', 'reduceShake')}
          {row('Disable flashing', 'noFlash')}
          {row('Colorblind palette', 'colorblind')}
          {row('Larger text', 'largeText')}
          <div class="eyebrow" style="margin-top:10px">Tutorial</div>
          <div class="row" style="padding-left:0;padding-right:0">
            <div class="row-main">
              <div style="font-weight:600;font-size:14.5px">First-steps tips</div>
              <div class="row-label" style="text-transform:none;letter-spacing:0.01em">Show the getting-started tips again on this device.</div>
            </div>
            <button class="btn btn-quiet" onClick={() => { tutorial.replay(); }}>Replay</button>
          </div>
        </div>
      </aside>
    </>
  );
}
